/**
 * 世界总装(美术 v2 版)。组合底盘 Stage + world 实体 + 效果调度,
 * 对外保持与旧版一致的契约:playEvent / dispatch / state / onEventPlayed,
 * 供模拟器(Phase 0)、HUD、以及未来 WebSocket(Phase 3)复用。
 */
import { Application } from 'pixi.js';
import { Stage, LW } from './Stage';
import { eventPool } from './ParticlePool';
import { TEX } from './Tex';
import { P } from './Palette';
import { sky } from './SkyRenderer';
import { clock } from './Ticker';
import { setScene } from './Scene';
import { Starfield } from '../world/Starfield';
import { Celestial } from '../world/Celestial';
import { CloudSea } from '../world/CloudSea';
import { Birds } from '../world/Birds';
import { Island } from '../world/Island';
import { FireSeed } from '../world/FireSeed';
import { CitizenCrowd } from '../world/Citizens';
import { Buildings } from '../world/Buildings';
import { Steles } from '../world/Steles';
import { EffectManager } from '../effects/EffectManager';
import { WorldState } from './WorldState';
import { EPOCHS, RENDER } from '../config';
import { sfx } from './Sfx';
import type { HeartbeatResult, WorldEvent, WorldStateSnapshot } from '../types';

export class World {
  app = new Application();
  state: WorldState;
  stage = new Stage();
  private celestial!: Celestial;
  private starfield!: Starfield;
  private cloudSea!: CloudSea;
  private birds!: Birds;
  private island!: Island;
  private fireSeed!: FireSeed;
  private buildings!: Buildings;
  private crowd!: CitizenCrowd;
  private steles!: Steles;
  private fx = new EffectManager();

  onEventPlayed: ((ev: WorldEvent) => void) | null = null;
  onSnapshot: ((s: WorldStateSnapshot) => void) | null = null;
  history: { type: WorldEvent['type']; midHash: string; at: number }[] = [];
  private evClock = 0;
  ready = false;   // WebGL/Stage 就绪;未就绪时数据仍更新,仅跳过画布渲染(P1-4 降级)

  constructor(private host: HTMLElement, private flavorHost: HTMLElement, state?: WorldState) {
    this.state = state ?? new WorldState();
  }

  async init() {
    const w = this.host.clientWidth || 960, h = this.host.clientHeight || 600;
    await this.app.init({
      width: w, height: h, background: RENDER.bg, antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2), autoDensity: true, powerPreference: 'high-performance',
    });
    this.host.appendChild(this.app.canvas);

    this.stage.init(this.app);
    this.stage.resize(w, h);

    this.celestial = new Celestial(this.stage);
    this.starfield = new Starfield(this.stage);
    this.cloudSea = new CloudSea(this.stage);
    this.birds = new Birds(this.stage);
    this.island = new Island(this.stage);
    this.fireSeed = new FireSeed(this.stage);
    const surf = (x: number) => this.island.surfaceYAt(x);
    this.buildings = new Buildings(this.stage, surf);
    this.crowd = new CitizenCrowd(this.stage, surf);
    this.steles = new Steles(this.stage, surf, (x) => { this.stage.shake.shake(1, 0.3); this.crowd.gather(x, 7); });
    setScene({ stage: this.stage, crowd: this.crowd, island: this.island, fireSeed: this.fireSeed, buildings: this.buildings });

    this.buildings.setEpoch(this.state.epochIndex);
    // 树属于"生命":生命纪(index≥2)前不存在(初始化瞬时,不播生长动画)
    this.island.setTreesGrown(this.state.epochIndex >= 2 ? 1 : 0, true);

    // 纪元跃迁 → 揭建筑 + 播大神迹 + 字幕(+ 生命纪:树生长、首批12烛民)
    this.state.onEpochAdvance = (_from, to) => {
      this.buildings.setEpoch(to);
      const ep = EPOCHS[to];
      this.emitFlavor(`第${to + 1}纪元 · ${ep.name} 降临`);
      if (to >= 2) {
        this.island.setTreesGrown(1);
        if (this.state.population < 12) this.state.setPopulation(12);
      }
      if (ep.effect) this.fx.trigger(ep.effect, 0.8, undefined, { epochName: ep.name, epochKey: ep.key });
    };

    const ro = new ResizeObserver(() => {
      const nw = this.host.clientWidth, nh = this.host.clientHeight;
      if (nw && nh) { this.app.renderer.resize(nw, nh); this.stage.resize(nw, nh); }
    });
    ro.observe(this.host);

    this.app.ticker.add((t) => this.frame(Math.min(t.deltaMS, 60) / 1000));
    this.ready = true;
  }

  private frame(dt: number) {
    // 混沌纪:世界灰暗待点亮(饱和/亮度压制,跃迁后 4s 缓退 = "世界上色")
    const chaosTarget = this.state.epochIndex === 0 ? 1 : 0;
    this.stage.colorGrade.chaos += (chaosTarget - this.stage.colorGrade.chaos) * Math.min(1, dt / 4);
    // 里程碑冲刺(≥85%):火种躁动,预告跃迁将至
    this.fireSeed.setUrgency(this.state.faithProgress() >= 0.85 && this.state.next() ? 1 : 0);
    this.stage.update(dt);
    this.island.update(dt);
    this.celestial.update(dt);
    this.starfield.update(dt);
    this.cloudSea.update(dt);
    this.birds.update(dt);
    this.fireSeed.update(dt);
    this.buildings.update(dt);
    this.crowd.setPopulation(this.state.population);
    this.crowd.update(dt);
    this.steles.sync(this.state.stones);
    this.steles.update(dt);
    this.fx.update(dt);
    if (this.onSnapshot) this.onSnapshot(this.state.snapshot());
  }

  // ── 对外契约 ──
  dispatch(res: HeartbeatResult) {
    res.events.forEach((ev) => this.playEvent(ev));
  }

  /**
   * 应用服务端权威快照(Phase 3):置数收敛多端 + 纪元推进时揭建筑/长树。
   * 大神迹与字幕由服务端事件经 dispatch/playEvent 另行播放,此处不重复触发。
   */
  applyServerState(s: {
    faith?: number; population?: number; online?: number; epochIndex?: number;
    weather?: WorldStateSnapshot['weather']; stones?: { id?: string; text: string; author: string; midHash?: string }[];
    seenTypes?: string[];
  }) {
    const advanced = this.state.applySnapshot(s);   // 置数总是执行(HUD 数字即便无 WebGL 也更新)
    if (!this.ready) return;                          // 无画布则到此为止
    if (typeof s.epochIndex === 'number') this.buildings.setEpoch(this.state.epochIndex);
    if (advanced && this.state.epochIndex >= 2) this.island.setTreesGrown(1);
  }

  playEvent(ev: WorldEvent) {
    if (this.ready) {                                // 画布渲染部分:无 WebGL 时跳过
      this.oracleDrop(ev);
      const meta = ev.type === 'epoch_leap' ? { epochName: this.state.epochName } : undefined;
      this.fx.trigger(ev.type, ev.intensity ?? 0.6, ev.duration_sec, meta);
    }
    if (ev.source?.mid_hash) this.history.push({ type: ev.type, midHash: ev.source.mid_hash, at: this.evClock++ });
    if (ev.carve_stone && ev.source?.raw) {
      this.state.carveStone(ev.source.raw, ev.source.uname ?? ev.source.mid_hash ?? '无名神明', ev.source.mid_hash);
      sfx.play('carve', { delay: 0.6 });
    }
    if (ev.flavor_text) {
      // 署名进世界:"我让它下雨了"是传播的核心记忆点,只在页面边缘的神谕流里
      // 记一笔远远不够——署名要出现在演出本身。
      const who = ev.source?.uname
        ? `神明 ${ev.source.uname}`
        : ev.source?.mid_hash
          ? `神明 ${ev.source.mid_hash.slice(0, 6)}`
          : undefined;
      this.emitFlavor(ev.flavor_text, who);
    }
    if (this.onEventPlayed) this.onEventPlayed(ev);
  }

  // ── 调试 ──
  setDebugHour(h: number | null) { clock.setDebugHour(h); }
  get sky() { return sky; }
  /** 手动步进 + 渲染(自动化/离屏取帧用;正常运行走 app.ticker) */
  step(n = 1, dt = 1 / 60) { for (let i = 0; i < n; i++) this.frame(dt); this.app.renderer.render(this.app.stage); }

  /** 神谕坠光:凡用户源(弹幕/评论/投币)的神谕,一道金色流光自天顶坠入岛面,
   *  落点绽开小小花火——"你的话真的落进了这个世界"的因果可见化。 */
  private oracleDrop(ev: WorldEvent) {
    const k = ev.source?.kind;
    if (k !== 'danmaku' && k !== 'comment' && k !== 'coin') return;
    const x = LW * (0.38 + Math.random() * 0.24);
    const gy = this.island.surfaceYAt(x) - 4;
    const fall = 520;
    eventPool?.spawn({
      x, y: -16, vy: fall, life: (gy + 16) / fall + 0.1, scale: 0.03, tint: P.divine, alpha: 0.95,
      blend: 'add', texture: TEX.core(), fade: 'none',
      onUpdate: (p) => {
        if (Math.random() < 0.5) {
          eventPool?.spawn({ x: p.x + (Math.random() - 0.5) * 4, y: p.y, vy: -26, life: 0.5, scale: 0.02, tint: P.divine, alpha: 0.5, blend: 'add', texture: TEX.glow(), fade: 'quad' });
        }
        if (!p.hit && p.y >= gy) {
          p.hit = true; p.life = 0;
          for (let i = 0; i < 10; i++) {
            const a = (i / 10) * Math.PI * 2;
            eventPool?.spawn({ x: p.x, y: gy, vx: Math.cos(a) * 70, vy: Math.sin(a) * 30 - 30, life: 0.6, scale: 0.022, tint: P.emberHi, alpha: 0.9, blend: 'add', texture: TEX.core(), fade: 'quad' });
          }
        }
      },
    });
  }

  // ── flavor 字幕:队列化(一次一条,后到排队),修复连发时同位置叠字 ──
  private flavorQueue: { text: string; who?: string }[] = [];
  private flavorBusy = false;

  private emitFlavor(text: string, who?: string) {
    if (this.flavorQueue.length > 5) this.flavorQueue.shift();  // 爆发时丢最旧,不无限积压
    this.flavorQueue.push({ text, who });
    this.pumpFlavor();
  }

  private pumpFlavor() {
    if (this.flavorBusy) return;
    const item = this.flavorQueue.shift();
    if (!item) return;
    this.flavorBusy = true;
    const el = document.createElement('div');
    el.className = 'flavor';
    el.style.left = '50%';
    if (item.who) {
      const w = document.createElement('span');
      w.className = 'who';
      w.textContent = item.who + ' · ';
      el.appendChild(w);
    }
    el.appendChild(document.createTextNode(item.text));  // textContent 路径,用户文本不走 innerHTML
    this.flavorHost.appendChild(el);
    window.setTimeout(() => el.remove(), 4300);
    window.setTimeout(() => { this.flavorBusy = false; this.pumpFlavor(); }, 2600);
  }
}
